<script lang="ts">
  import { signOutUser } from '../lib/firebase';
  import { user } from '../lib/stores';
  import BetaTag from './BetaTag.svelte';
  import { submitCode, accessError, type AccessState } from '../lib/accessControl';

  let { state: access }: { state: AccessState } = $props();
  const blocked = $derived(access === 'blocked');
  const needsCode = $derived(access === 'code_required');

  let code = $state('');
  let busy = $state(false);
  async function activate() {
    const c = code.replace(/[^0-9a-fA-F]/g, '');
    if (c.length !== 16) { accessError.set('The activation code is 16 characters (0–9, a–f).'); return; }
    busy = true;
    try { await submitCode($user, c); } finally { busy = false; }
  }
</script>

<div class="gate">
  <div class="card">
    <div class="brandrow"><span class="brand">empowered</span><BetaTag /></div>
    {#if blocked}
      <h1>Access turned off</h1>
      <p class="sub">Your access to this record has been disabled. If you believe this is a mistake, contact your administrator.</p>
    {:else if needsCode}
      <h1>Enter your activation code</h1>
      <p class="sub">Your access has been approved. Enter the 16-character code your administrator gave you to activate this account.</p>
      <form class="codeform" onsubmit={(e) => { e.preventDefault(); void activate(); }}>
        <input
          class="codein" type="text" inputmode="text" autocomplete="one-time-code"
          spellcheck="false" maxlength="19" placeholder="xxxx xxxx xxxx xxxx"
          bind:value={code} disabled={busy}
        />
        <button class="go" type="submit" disabled={busy}>{busy ? 'Checking…' : 'Activate'}</button>
      </form>
      {#if $accessError}<p class="err">{$accessError}</p>{/if}
    {:else}
      <h1>By invitation</h1>
      <p class="sub">empowered is currently invitation-only. Your request has been received and is pending — you'll be able to continue once your access is approved.</p>
    {/if}
    <button class="out" onclick={() => signOutUser()}>Sign out</button>
    <p class="legal faint">Signed in, awaiting access.</p>
  </div>
</div>

<style>
  .gate { min-height: 100dvh; display: grid; place-items: center; padding: var(--space-6); background: radial-gradient(130% 80% at 50% -10%, color-mix(in srgb, var(--accent) 10%, var(--surface)) 0%, var(--bg) 55%); }
  .card { width: min(100%, 400px); display: flex; flex-direction: column; align-items: center; text-align: center; gap: var(--space-3); padding: var(--space-8); background: color-mix(in srgb, var(--surface) 80%, transparent); border: 1px solid var(--line); border-radius: var(--radius-lg); }
  .brandrow { display: inline-flex; align-items: center; gap: var(--space-2); }
  .brand { font-family: var(--font-brand); font-weight: 400; letter-spacing: 0.03em; color: var(--accent); font-size: var(--fs-xs); }
  h1 { font-size: var(--fs-2xl); font-weight: 800; letter-spacing: -0.02em; }
  .sub { color: var(--text-2); font-size: var(--fs-sm); max-width: 34ch; line-height: 1.55; }
  .codeform { display: flex; flex-direction: column; gap: var(--space-2); width: 100%; }
  .codein { width: 100%; text-align: center; font-family: var(--mono, monospace); font-size: var(--fs-md); letter-spacing: 0.12em; padding: var(--space-3); background: var(--surface-2); border: 1px solid var(--line-strong); border-radius: var(--radius-md); color: var(--text); }
  .codein:focus { outline: none; border-color: var(--accent); }
  .go { padding: var(--space-3) var(--space-5); border-radius: var(--radius-pill); background: var(--accent); color: var(--on-accent, #14161A); font-weight: 800; font-size: var(--fs-sm); }
  .go:disabled { opacity: 0.6; }
  .err { color: var(--danger, #e5484d); font-size: var(--fs-xs); max-width: 34ch; line-height: 1.5; }
  .out { margin-top: var(--space-2); padding: var(--space-3) var(--space-5); border-radius: var(--radius-pill); background: var(--surface-2); border: 1px solid var(--line-strong); color: var(--text); font-weight: 700; font-size: var(--fs-sm); }
  .out:hover { border-color: var(--accent); color: var(--accent); }
  .legal { font-size: var(--fs-2xs); margin-top: var(--space-2); }
</style>
